import { DateTime } from 'luxon'

/** The fields that say when an event happens. Changing any of them is a change of the event's time. */
export interface EventTimes {
  start: Date | string | null
  end: Date | string | null
  date: Date | string | null
  allDay: boolean
}

/**
 * What a manager may still change on an event. See docs/events.md, _Editing_.
 *
 * - `all` - nothing: the event is cancelled, has ended, or is in progress and someone is signed up.
 * - `time` - everything except `start`/`end`/`date`/`allDay`, because participants signed up for
 *   that time. A separate kind so that a later "notify or cancel the participants" flow can offer
 *   a way through this one lock without touching the others.
 */
export type EventEditLock =
  | { kind: 'none' }
  | { kind: 'all'; reason: 'event.cancelled' | 'event.ended' | 'event.ongoingWithParticipants' }
  | { kind: 'time'; participants: number }

const toDateTime = (value: Date | string | null): DateTime | null =>
  value === null ? null : DateTime.fromJSDate(new Date(value))

/**
 * The instants an event occupies. An all-day event normally carries `start`/`end` spanning its day,
 * but older rows may not, so it falls back to `date`'s day in the premise's timezone.
 */
export function eventPeriod(event: EventTimes, timezone: string): { start: DateTime; end: DateTime } | null {
  const start = toDateTime(event.start)
  const end = toDateTime(event.end)
  if (start && end) {
    return { start, end }
  }
  const day = event.allDay ? toDateTime(event.date)?.setZone(timezone) : null
  return day ? { start: day.startOf('day'), end: day.endOf('day') } : null
}

/** Where an event is in time. `null` for a row with no times at all. */
export type EventPhase = 'upcoming' | 'ongoing' | 'ended'

/**
 * The one definition of upcoming / ongoing / ended, shared by the edit lock, the events list, the
 * detail page and the participant rules. The start instant is ongoing; the end instant has ended.
 */
export function eventPhase(event: EventTimes, now: Date, timezone: string): EventPhase | null {
  const period = eventPeriod(event, timezone)
  if (!period) {
    return null
  }
  const at = DateTime.fromJSDate(now)
  if (period.end <= at) {
    return 'ended'
  }
  return period.start <= at ? 'ongoing' : 'upcoming'
}

/**
 * Decides what may be edited. `participants` counts everyone signed up - confirmed or on the waiting
 * list (`countEventParticipants` on the backend, `confirmedCount + waitingCount` on the frontend).
 *
 * The end instant belongs to the past: an event whose `end` is now has ended. A cancelled event is locked whole: the
 * cancel is one-way (docs/events.md, _Cancelling an event_).
 */
export function eventEditLock(
  event: EventTimes & { cancelledAt?: Date | string | null },
  participants: number,
  now: Date,
  timezone: string,
): EventEditLock {
  if (event.cancelledAt) {
    return { kind: 'all', reason: 'event.cancelled' }
  }
  const phase = eventPhase(event, now, timezone)
  if (phase === 'ended') {
    return { kind: 'all', reason: 'event.ended' }
  }
  if (phase === 'ongoing' && participants > 0) {
    return { kind: 'all', reason: 'event.ongoingWithParticipants' }
  }
  if (participants > 0) {
    return { kind: 'time', participants }
  }
  return { kind: 'none' }
}

const sameInstant = (a: Date | string | null, b: Date | string | null) =>
  (a === null ? null : new Date(a).getTime()) === (b === null ? null : new Date(b).getTime())

/**
 * Whether `next` moves the event in time. It compares values, not which fields were sent, so
 * resending the stored times unchanged is not a change. A timed event's `date` and an all-day
 * event's `start`/`end` are derived, so only the pair that defines the event is compared.
 */
export function eventTimeChanged(stored: EventTimes, next: EventTimes): boolean {
  if (stored.allDay !== next.allDay) {
    return true
  }
  if (next.allDay) {
    return !sameInstant(stored.date, next.date)
  }
  return !sameInstant(stored.start, next.start) || !sameInstant(stored.end, next.end)
}
